import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Film, Swords } from 'lucide-react';

import { useGetMyMatchesQuery, type MatchSummary } from '@/services/qchinesepokerApi';
import { postoMio } from '@/lib/qcp/rest';
import { useHaptics } from '@/hooks/useHaptics';
import { useQcpMe } from '@/hooks/useQcpMe';
import { QcpRestNotice } from '@/components/chinesepoker/QcpRestNotice';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';

function outcomeFor(m: MatchSummary, mySeat: 'A' | 'B' | null): 'won' | 'lost' | 'draw' | 'unknown' {
  if (m.winnerSeat === null) return 'draw';
  if (!mySeat) return 'unknown';
  return m.winnerSeat === mySeat ? 'won' : 'lost';
}

/** Hub "Partite" section: the caller's match history with result + replay. */
export function QcpMatchesPanel() {
  const { t, i18n } = useTranslation('chinesepoker');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const mioUserId = useQcpMe();
  const { data: matches, isLoading, isError, error, refetch } = useGetMyMatchesQuery({ limit: 20 });
  const failed = isError || matches === null;
  const rows = matches ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-2">
      {isLoading && (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      )}
      {!isLoading && failed && (
        <QcpRestNotice
          error={error}
          fallback={t('hubPanels.matchesError', { defaultValue: 'Could not load your matches. Sign in again if this persists.' })}
          onRetry={() => void refetch()}
        />
      )}
      {!isLoading && !failed && rows.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center">
          <Swords aria-hidden className="h-6 w-6 text-fg-muted" />
          <p className="text-sm text-fg-muted">
            {t('hubPanels.matchesEmpty', { defaultValue: 'No matches yet — play your first hand!' })}
          </p>
        </div>
      )}
      {rows.map((m) => {
        const mySeat = postoMio(m.players, mioUserId);
        const outcome = outcomeFor(m, mySeat);
        const oppSeat = mySeat === 'A' ? 'B' : mySeat === 'B' ? 'A' : 'B';
        const oppName = mySeat ? m.players[oppSeat].displayName : `${m.players.A.displayName} · ${m.players.B.displayName}`;
        const net = mySeat ? m.net[mySeat] : null;
        return (
          <div key={m.matchId} className="flex items-center gap-2 rounded-2xl bg-bg-secondary p-3">
            <button
              type="button"
              onClick={() => {
                haptics.selection();
                navigate(`/chinesepoker/result/${m.matchId}`);
              }}
              className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-80"
            >
              <span
                className={cn(
                  'w-10 shrink-0 text-center text-xs font-bold uppercase',
                  outcome === 'won' && 'text-success',
                  outcome === 'lost' && 'text-danger',
                  (outcome === 'draw' || outcome === 'unknown') && 'text-fg-muted',
                )}
              >
                {t(`hubPanels.outcome.${outcome}`, {
                  defaultValue: outcome === 'won' ? 'W' : outcome === 'lost' ? 'L' : '—',
                })}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-fg">
                  {oppName}
                </span>
                <span className="block text-xs text-fg-muted">
                  {net !== null && (<>{net > 0 ? `+${net}` : net} · </>)}{new Date(m.finishedAt).toLocaleDateString(i18n.language)}
                </span>
              </span>
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" />
            </button>
            <button
              type="button"
              aria-label={t('hubPanels.replay', { defaultValue: 'Replay' })}
              onClick={() => {
                haptics.selection();
                navigate(`/chinesepoker/replay/${m.matchId}`);
              }}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-fg-muted transition-opacity active:opacity-70"
            >
              <Film aria-hidden className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
